import { combineLatest, Observable, of } from 'rxjs';
import { map, shareReplay, startWith, switchMap } from 'rxjs/operators';
import { VariableBenefitPaymentFrequencyEnum } from '../../../../../models/enum/shared/variable-benefit-payment-frequency.enum';
import { RecurringPaymentInstruction } from '../../../../../models/submissions/recurring-payment-instruction';
import { VariableBenefitAccountDetails } from '../../../../../models/transactions/variable-benefit-account-details';
import { VbPaymentChangeRecurringPaymentFormGroup } from '../../../../../models/shared/forms/vb-payment-change-recurring-payment-form-group';
import { VbPaymentChangePaymentStepFormGroup } from '../../../../../models/shared/forms/vb-payment-change-payment-step-form-group';
import { RecurringPaymentAmountTypeEnum } from '../../../../../models/enum/shared/recurring-payment-amount-type.enum';
import { AnnualMinimumDetails } from '../../../../../models/transactions/annual-minimum-details';
import { MemberVbAccountEstimatedAnnualPaymentTotalRequest } from '../../../../../models/transactions/member-vb-account-estimated-annual-payment-total-request';
import {
  LumpSumPaymentInstructionChangeRequest,
  RecurringPaymentInstructionChangeRequest
} from '../../../../../models/users/requests/submissionRequests/create-variable-benefit-payment-change-request';
import { DateUtils } from '../../../../../utils/date-utils';
import { DateConversionUtils } from '../../../../../utils/date-conversion-utils';
import { NumberUtils } from '../../../../../utils/number-utils';
import { VbPaymentChangeFormFactory } from './vb-payment-change-form-factory';

export interface ScheduledPaymentChange<T> {
  changed: boolean;
  payment?: T;
}

export type RecurringPaymentChange = ScheduledPaymentChange<RecurringPaymentInstructionChangeRequest>;
export type LumpSumPaymentChange = ScheduledPaymentChange<LumpSumPaymentInstructionChangeRequest>;

export interface ForgoRecurringPaymentFormSetter {
  setMonthlyRecurringPaymentForm(form: VbPaymentChangeRecurringPaymentFormGroup | null): void;
  setAnnualRecurringPaymentForm(form: VbPaymentChangeRecurringPaymentFormGroup | null): void;
}

export function payingInstructionOrNull(
  instruction: RecurringPaymentInstruction | null | undefined
): RecurringPaymentInstruction | null {
  return VbPaymentChangePaymentsStepUtils.paysSomething(instruction) ? instruction : null;
}

export class VbPaymentChangePaymentsStepUtils {
  static toRecurringPaymentChange(
    form: VbPaymentChangeRecurringPaymentFormGroup
  ): RecurringPaymentInstructionChangeRequest | undefined {
    const { paymentAmount, paymentStartYear, paymentStartMonth, recurringPaymentAmountType } = form.getRawValue();
    const useMinimumAmount = recurringPaymentAmountType === RecurringPaymentAmountTypeEnum.Minimum;
    // eslint-disable-next-line eqeqeq
    if ((paymentAmount == null && !useMinimumAmount) || paymentStartYear == null || paymentStartMonth == null) {
      return undefined;
    }
    return new RecurringPaymentInstructionChangeRequest(
      VbPaymentChangePaymentsStepUtils.firstOfMonth(paymentStartYear, paymentStartMonth),
      paymentAmount ?? 0,
      useMinimumAmount
    );
  }

  static toLumpSumPaymentChange(
    amount: number | null | undefined,
    year: number | null | undefined,
    month: number | null | undefined
  ): LumpSumPaymentInstructionChangeRequest | undefined {
    // eslint-disable-next-line eqeqeq
    if (amount == null || year == null || month == null) {
      return undefined;
    }
    return new LumpSumPaymentInstructionChangeRequest(
      VbPaymentChangePaymentsStepUtils.firstOfMonth(year, month),
      amount
    );
  }

  static estimateRequestFields(
    minimumAgeDetails: AnnualMinimumDetails,
    monthly: RecurringPaymentChange,
    annual: RecurringPaymentChange,
    lumpSum: LumpSumPaymentChange
  ): MemberVbAccountEstimatedAnnualPaymentTotalRequest {
    return {
      minimumAgeDateOfBirth: minimumAgeDetails.minimumAgeDateOfBirth,
      minimumAgeSpouseId: minimumAgeDetails.minimumAgeSpouseId,
      monthlyRecurringPaymentInstructionChange: monthly.changed ? monthly.payment ?? null : null,
      annualRecurringPaymentInstructionChange: annual.changed ? annual.payment ?? null : null,
      lumpSumPaymentInstructionChange: lumpSum.changed ? lumpSum.payment ?? null : null
    };
  }

  static withRecurringPaymentChange<T extends MemberVbAccountEstimatedAnnualPaymentTotalRequest>(
    req: T,
    frequency: VariableBenefitPaymentFrequencyEnum,
    change: RecurringPaymentInstructionChangeRequest
  ): T {
    return frequency === VariableBenefitPaymentFrequencyEnum.Monthly
      ? { ...req, monthlyRecurringPaymentInstructionChange: change }
      : { ...req, annualRecurringPaymentInstructionChange: change };
  }

  // A stopped recurring payment is stored as a $0 instruction, so an instruction alone doesn't mean anything is paid.
  static paysSomething(
    instruction: RecurringPaymentInstruction | null | undefined
  ): instruction is RecurringPaymentInstruction {
    return !!instruction && (instruction.useMinimumAmount || instruction.paymentAmount > 0);
  }

  static isMissingRecurringPayment(
    monthlyForm: VbPaymentChangeRecurringPaymentFormGroup | null,
    annualForm: VbPaymentChangeRecurringPaymentFormGroup | null,
    activeMonthly: RecurringPaymentInstruction | null,
    activeAnnual: RecurringPaymentInstruction | null,
    forgoRecurringPayments: boolean
  ): boolean {
    const hasRecurringConfigured =
      !!monthlyForm ||
      !!annualForm ||
      VbPaymentChangePaymentsStepUtils.paysSomething(activeMonthly) ||
      VbPaymentChangePaymentsStepUtils.paysSomething(activeAnnual) ||
      forgoRecurringPayments;
    if (!hasRecurringConfigured) return true;

    return (
      VbPaymentChangePaymentsStepUtils.isStagedZeroWithNothingToEnd(monthlyForm, activeMonthly) ||
      VbPaymentChangePaymentsStepUtils.isStagedZeroWithNothingToEnd(annualForm, activeAnnual)
    );
  }

  static annualPaymentOverBalance$(
    annualForm$: Observable<VbPaymentChangeRecurringPaymentFormGroup | null>,
    paymentStepForm$: Observable<VbPaymentChangePaymentStepFormGroup>,
    details$: Observable<VariableBenefitAccountDetails>
  ): Observable<boolean> {
    return combineLatest([
      annualForm$.pipe(
        switchMap(
          form =>
            form?.valueChanges.pipe(
              startWith(null),
              map(() => form)
            ) ?? of(null)
        )
      ),
      paymentStepForm$.pipe(
        switchMap(form =>
          form.valueChanges.pipe(
            startWith(null),
            map(() => form.getRawValue().lumpSumPaymentAmount)
          )
        )
      ),
      details$
    ]).pipe(
      map(([annualForm, lumpSumAmount, details]) =>
        VbPaymentChangePaymentsStepUtils.isAnnualPaymentOverBalance(
          annualForm,
          details.activeAnnualRecurringPaymentInstruction,
          lumpSumAmount,
          details.totalBalance
        )
      ),
      shareReplay({ bufferSize: 1, refCount: true })
    );
  }

  static isAnnualPaymentOverBalance(
    annualForm: VbPaymentChangeRecurringPaymentFormGroup | null,
    activeAnnual: RecurringPaymentInstruction | null,
    lumpSumAmount: number | null,
    balance: number
  ): boolean {
    if (!annualForm?.dirty) return false;
    const { paymentAmount, paymentStartYear, recurringPaymentAmountType } = annualForm.getRawValue();
    if (
      recurringPaymentAmountType === RecurringPaymentAmountTypeEnum.Minimum ||
      paymentStartYear !== DateUtils.currentYearInCST() ||
      !paymentAmount
    ) {
      return false;
    }
    const isCurrentAmount =
      !!activeAnnual && !activeAnnual.useMinimumAmount && activeAnnual.paymentAmount === paymentAmount;
    if (isCurrentAmount) return false;
    return NumberUtils.sumAmounts([paymentAmount, lumpSumAmount]) > NumberUtils.roundToCents(balance);
  }

  // When the user removes a scheduled payment change, we need the backend to delete the scheduled instruction.
  // The backend interprets a submitted payment that matches the active amount as a deletion of the scheduled.
  // We use the active instruction's amount (to trigger that match) paired with the scheduled instruction's
  // startDate (to avoid past-date validation errors, since the active's startDate may be in the past).
  static buildRemovalPayment(
    active: RecurringPaymentInstruction | null,
    scheduled: RecurringPaymentInstruction | null
  ): RecurringPaymentInstructionChangeRequest | undefined {
    if (!active || !scheduled) return undefined;
    return new RecurringPaymentInstructionChangeRequest(
      scheduled.paymentStartDate.substring(0, 10),
      active.paymentAmount,
      active.useMinimumAmount
    );
  }

  static isPastCutoff(cutoffDate: string | null | undefined, today: string = DateUtils.todayInCST()): boolean {
    if (!cutoffDate) return false;
    return today > cutoffDate.substring(0, 10);
  }

  // Past this month's cutoff, payments scheduled for next month are already being processed and can't be changed.
  static isLockedInPastCutoff(paymentDate: string | null | undefined, cutoffDate: string | null): boolean {
    if (!paymentDate || !VbPaymentChangePaymentsStepUtils.isPastCutoff(cutoffDate)) return false;
    const today = DateUtils.parseDateOnlyAsLocalMidnight(DateUtils.todayInCST());

    const scheduledDate = DateUtils.parseDateOnlyAsLocalMidnight(paymentDate.split('T')[0]);
    const nextMonth = new Date(today.getFullYear(), today.getMonth() + 1, 1);
    return scheduledDate.getFullYear() === nextMonth.getFullYear() && scheduledDate.getMonth() === nextMonth.getMonth();
  }

  static createForgoRecurringPaymentForms(
    details: VariableBenefitAccountDetails,
    allowableChangeDate: Date,
    formSetter: ForgoRecurringPaymentFormSetter
  ): void {
    const startMonth = allowableChangeDate.getMonth() + 1;
    const startYear = allowableChangeDate.getFullYear();

    if (VbPaymentChangePaymentsStepUtils.paysSomething(details.activeMonthlyRecurringPaymentInstruction)) {
      formSetter.setMonthlyRecurringPaymentForm(
        VbPaymentChangeFormFactory.buildForgoRecurringPaymentFormGroup(
          VariableBenefitPaymentFrequencyEnum.Monthly,
          startMonth,
          startYear
        )
      );
    } else {
      formSetter.setMonthlyRecurringPaymentForm(null);
    }

    if (VbPaymentChangePaymentsStepUtils.paysSomething(details.activeAnnualRecurringPaymentInstruction)) {
      if (
        VbPaymentChangePaymentsStepUtils.annualPaymentHasOccurredThisYear(
          details.activeAnnualRecurringPaymentInstruction
        )
      ) {
        const nextJanuary = new Date(DateUtils.currentYearInCST() + 1, 0, 1);
        const annualStop = allowableChangeDate > nextJanuary ? allowableChangeDate : nextJanuary;
        formSetter.setAnnualRecurringPaymentForm(
          VbPaymentChangeFormFactory.buildForgoRecurringPaymentFormGroup(
            VariableBenefitPaymentFrequencyEnum.Annually,
            annualStop.getMonth() + 1,
            annualStop.getFullYear()
          )
        );
      } else {
        formSetter.setAnnualRecurringPaymentForm(
          VbPaymentChangeFormFactory.buildForgoRecurringPaymentFormGroup(
            VariableBenefitPaymentFrequencyEnum.Annually,
            startMonth,
            startYear
          )
        );
      }
    } else {
      formSetter.setAnnualRecurringPaymentForm(null);
    }
  }

  static annualPaymentHasOccurredThisYear(instruction: RecurringPaymentInstruction | null | undefined): boolean {
    const start = DateConversionUtils.parseDateOnlyParts(instruction?.paymentStartDate ?? '');
    const today = DateConversionUtils.parseDateOnlyParts(DateUtils.todayInCST());
    if (!start || !today) return false;
    return start.year <= today.year && start.month <= today.month;
  }

  static firstOfMonth(year: number, month: number): string {
    return `${year}-${String(month).padStart(2, '0')}-01`;
  }

  private static isStagedZeroWithNothingToEnd(
    form: VbPaymentChangeRecurringPaymentFormGroup | null,
    activeInstruction: RecurringPaymentInstruction | null
  ): boolean {
    if (!form) return false;
    if (form.controls.recurringPaymentAmountType.value === RecurringPaymentAmountTypeEnum.Minimum) return false;
    return (
      (form.controls.paymentAmount.value ?? 0) === 0 &&
      !VbPaymentChangePaymentsStepUtils.paysSomething(activeInstruction)
    );
  }
}
